import { Search as SearchIcon } from '@/constants/hardfin-standard-icons';
import { Location } from '@/location/api/types';
import { useLocations } from '@/location/hooks/use-locations';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { SparseZone } from '@/sparse/api/types';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Chip,
  Divider,
  InputAdornment,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { debounce, sortBy } from 'lodash-es';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';

const MAX_LIST_HEIGHT = 400;
const ESTIMATED_ITEM_HEIGHT = 64;
const SEARCH_DEBOUNCE_MS = 300;
const ZONE_INDENT_SPACING = 4;

// Every row is estimated at the same height until it is measured, so this never varies per
// render and lives outside the component
const estimateSize = () => ESTIMATED_ITEM_HEIGHT;

// Archived records are fetched up front but stay hidden until a search asks for something
// specific, so browsing the list is not cluttered by them
const LOCATION_FILTER = { isArchived: 'all' } as const;

interface Props {
  onLocationSelect: (locationKey: string) => void;
  excludeLocationId?: string;
  /** A record that is listed but cannot be picked, with the reason shown on hover */
  blocked?: { locationKey: string; reason: string };
}

interface SelectionItem {
  id: string;
  key: string;
  name: string;
  isZone: boolean;
  isArchived: boolean;
  detail: string;
  /** Rendered as a plain label: the site owns the zones below it but cannot be picked */
  isHeader?: boolean;
}

function siteItem(site: Location): SelectionItem {
  return {
    id: site.id,
    key: site.key,
    name: getEffectiveLocationName(site),
    isZone: false,
    isArchived: site.isArchived,
    detail: `${pluralize(site.activeProjectCount, { singular: 'project' })}, ${pluralize(site.assetCount, { singular: 'asset' })}`,
  };
}

// Used when the site itself cannot be picked but its zones can, so the zones still sit under
// the name of the site that owns them
function siteHeaderItem(site: Location): SelectionItem {
  return {
    id: site.id,
    key: site.key,
    // The bare site name: a header labels the zones under it and does not offer the site's
    // own default bucket, so the " — Default" suffix would be misleading here
    name: site.name,
    isZone: false,
    isArchived: site.isArchived,
    detail: '',
    isHeader: true,
  };
}

function zoneItem(zone: SparseZone): SelectionItem {
  return {
    id: zone.id,
    key: zone.key,
    name: zone.name,
    isZone: true,
    isArchived: zone.isArchived,
    detail: pluralize(zone.assetCount, { singular: 'asset' }),
  };
}

export function LocationSelectionList(props: Props) {
  const { locations } = useLocations({ filter: LOCATION_FILTER });
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        setDebouncedSearch(searchValue);
      }, SEARCH_DEBOUNCE_MS),
    [setDebouncedSearch]
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchValue(value);
      debouncedSetSearch(value);
    },
    [setSearchValue, debouncedSetSearch]
  );

  // Zones render under the site that owns them, so the tree is flattened into one list the
  // virtualizer can measure by index
  const flatItems = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase();
    const isSearching = search !== '';
    const matches = (value: string) =>
      !isSearching || value.toLowerCase().includes(search);
    // An archived record is only worth offering when the search names it. A zone is reachable
    // only through its site, so an archived site takes its zones out of the browse list too.
    const isOffered = (record: { id: string; isArchived: boolean }) =>
      record.id !== props.excludeLocationId &&
      (isSearching || !record.isArchived);

    const items: SelectionItem[] = [];

    for (const site of sortBy(locations, (location) =>
      getEffectiveLocationName(location).toLowerCase()
    )) {
      if (!isSearching && site.isArchived) continue;

      const zones = sortBy(site.zones.filter(isOffered), (zone) =>
        zone.name.toLowerCase()
      );

      const siteOffered = isOffered(site);
      // The synthesized " — Default" suffix is not on any record, so searching matches the
      // site's own name and the suffix is display only
      const siteMatches = siteOffered && matches(site.name);
      const matchingZones = zones.filter((zone) => matches(zone.name));

      if (!siteMatches && matchingZones.length === 0) continue;

      // A zone never stands on its own: the site it belongs to always renders above it, as a
      // plain header when the site itself is not something you can pick
      if (siteOffered) {
        items.push(siteItem(site));
      } else {
        items.push(siteHeaderItem(site));
      }

      // A site kept only because one of its zones matched shows just those zones
      for (const zone of siteMatches ? zones : matchingZones) {
        items.push(zoneItem(zone));
      }
    }

    return items;
  }, [locations, props.excludeLocationId, debouncedSearch]);

  const getScrollElement = useCallback(() => scrollRef.current, []);
  const getItemKey = useCallback(
    (index: number) => flatItems[index].key,
    [flatItems]
  );

  const virtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement,
    estimateSize,
    getItemKey,
    overscan: 10,
    initialRect: { width: 0, height: MAX_LIST_HEIGHT },
  });

  return (
    <Stack spacing={2}>
      <TextField
        placeholder="Search locations and zones"
        value={searchValue}
        onChange={handleSearchChange}
        variant="outlined"
        size="small"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Paper variant="outlined">
        <div
          ref={scrollRef}
          style={{ maxHeight: MAX_LIST_HEIGHT, overflow: 'auto' }}
        >
          {flatItems.length === 0 ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography variant="body2" color="text.secondary">
                No locations found for {searchValue}
              </Typography>
            </Box>
          ) : (
            <div
              style={{
                height: virtualizer.getTotalSize(),
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const item = flatItems[virtualItem.index];
                return (
                  <div
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: virtualItem.start,
                      left: 0,
                      width: '100%',
                    }}
                  >
                    <SelectionRow
                      item={item}
                      blockedReason={
                        props.blocked?.locationKey === item.key
                          ? props.blocked.reason
                          : undefined
                      }
                      onSelect={() => props.onLocationSelect(item.key)}
                    />
                    {virtualItem.index < flatItems.length - 1 && (
                      <Divider sx={{ width: '90%', mx: 'auto' }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Paper>
    </Stack>
  );
}

interface SelectionRowProps {
  item: SelectionItem;
  blockedReason?: string;
  onSelect: () => void;
}

function SelectionRow(props: SelectionRowProps) {
  if (props.item.isHeader) {
    return (
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }} display="flex" gap={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {props.item.name}
        </Typography>
        {props.item.isArchived && (
          <Chip size="small" label="Archived" color="default" />
        )}
      </Box>
    );
  }

  const row = (
    <ListItemButton
      onClick={props.onSelect}
      disabled={props.blockedReason !== undefined}
      sx={{ pl: props.item.isZone ? ZONE_INDENT_SPACING : 2 }}
    >
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant={props.item.isZone ? 'body2' : 'body1'}>
              {props.item.name}
            </Typography>
            {props.item.isZone && (
              <Chip size="small" label="Zone" variant="outlined" />
            )}
            {props.item.isArchived && (
              <Chip size="small" label="Archived" color="default" />
            )}
          </Box>
        }
        secondary={
          <Typography variant="caption" color="text.secondary">
            {props.item.detail}
          </Typography>
        }
      />
    </ListItemButton>
  );

  if (props.blockedReason === undefined) {
    return row;
  }

  // A disabled button emits no pointer events, so the tooltip listens on a wrapper
  return (
    <Tooltip arrow title={props.blockedReason}>
      <span>{row}</span>
    </Tooltip>
  );
}
